import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { retry } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { TokenResult } from '../models/token-result';
import { PortalStorageService } from '@app/core/services/storage/portal-storage.service';
import { ApiBuilder } from '@app/core/helpers/api/api-builder';
import { Constants } from '@app/core/config/constants';
import { DeviceService } from '../../../services/device.service';
import Swal from 'sweetalert2';
import { confirmDialogConfig } from './../../../helpers/util/alert-handler';
import { DialogType } from './../../../helpers/enum/app.enums';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private tokenResultSubject: BehaviorSubject<TokenResult>;
    public tokenResult: Observable<TokenResult>;
    errorMessage: any;
    public isTokenValid = false;

    private refreshTokenTimeout: any;

    constructor(
        private router: Router,
        private http: HttpClient,
        private storageService: PortalStorageService,
        private deviceService: DeviceService,
    ) {
        this.tokenResultSubject = new BehaviorSubject<TokenResult>(null);
        this.tokenResult = this.tokenResultSubject.asObservable();
    }

    public get tokenValue(): TokenResult {
        return this.tokenResultSubject.value;
    }

    public get loggedIn(): boolean {
        return this.storageService.getToken() !== null;
    }

    login(username: string, appPassword: string): any {
        const request = JSON.stringify({
            userName: username,
            password: appPassword,
            appTypeId: this.deviceService.appTypeId,
            deviceInfo: `${this.deviceService.deviceInfo}`,
        });

        const headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http.post<any>(ApiBuilder.createUrl(Constants.API_PORTAL_TOKEN_CREATE), request, headers).pipe(
            map((token) => {
                this.tokenResultSubject.next(token);
                this.storageService.saveToken(token);
                this.storageService.saveRefreshToken(token.refreshToken);

                // console.log(token);
                return token;
            }),
            retry(1),
        );
    }

    // TODO: iser refrshtoken to renew the auth
    renew(): Observable<boolean> {
        const refreshToken = this.storageService.getRefreshToken();
        if (refreshToken != null) {
            const request = JSON.stringify({
                refreshToken: refreshToken,
                appType: this.deviceService.appTypeId,
                deviceInfo: this.deviceService.deviceInfo,
            });

            const headers = {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                }),
            };

            return this.http.post<any>(ApiBuilder.createUrl(Constants.API_PORTAL_TOKEN_RENEW), request, headers).pipe(
                map((token) => {
                    this.tokenResultSubject.next(token);
                    this.storageService.saveToken(token);
                    this.storageService.saveRefreshToken(token.refreshToken);
                    return true;
                }),
                retry(1),
            );
        } else {
            return of(false);
        }
    }

    logout(): void {
        Swal.fire(
            confirmDialogConfig('Warning!', 'Are you sure you want to log out?', DialogType.Warning, 'All the current sessions will be logged out.'),
        ).then((willContinue) => {
            if (!willContinue.dismiss) {
                this.tokenResultSubject.next(null);
                this.storageService.clearAppAuthStorage();
                this.router.navigate(['/login']);
            }
        });
    }

    validateToken(expire: Date): void {
        if (new Date(expire) > new Date()) {
            this.isTokenValid = true;
        }
    }

    public autoAuthenticate(aToken: string, userId: string): any {
        // verify user using existing access token
        return this.http
            .get<any>(`${ApiBuilder.createUrl(Constants.API_PORTAL_TOKEN_VERIFY)}/${userId}/${this.deviceService.appTypeId}`, {
                headers: new HttpHeaders({
                    Authorization: 'Bearer ' + aToken,
                    'Content-Type': 'application/json',
                }),
            })
            .pipe(
                map((token) => {
                    this.tokenResultSubject.next(token);
                    this.storageService.saveToken(token);
                    this.storageService.saveRefreshToken(token.refreshToken);
                    // console.log(token);
                    return token;
                }),
            );
    }

    public isAuthenticated(): boolean {
        const token = this.storageService.getToken();
        if (token !== null) {
            this.tokenResultSubject.next(token);
            this.validateToken(token.expire);
        }
        return this.isTokenValid;
    }

    public getRefreshToken(): string {
        return this.storageService.getRefreshToken();
    }

    public getAccessToken(): string {
        return this.storageService.getAccessToken();
    }

    public getUserId(): string {
        return this.storageService.getUserId();
    }

    public getUserName(): string {
        return this.storageService.getUserName();
    }

    public removeAuth(): void {
        return this.storageService.removeAuth();
    }
}
